import { all, call, getContext, put, select, takeLatest } from 'redux-saga/effects';
import { pConfirmationDocEditorActions } from './slice';
import { manuallyDecrementPromiseCounter, manuallyIncrementPromiseCounter, trackPromise } from 'react-promise-tracker';
import { httpClientError, httpSuccess } from 'app/services/serviceHelpers';
import { TemplateService } from 'app/services/TemplateService/template-service';
import { trackException } from 'app-insights/appInsightsTracking';
import { ExceptionType } from 'app/enums/Common';
import { PayloadAction } from '@reduxjs/toolkit';
import { PlacementService } from 'app/services/PlacementServices/placement-service';
import { selectUser } from 'oidc/user.selectors';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { selectContacts, selectDocumentList, selectPageAttributes, selectTemplates } from './selector';
import _ from 'lodash';
import { IConfirmationDocPayload, IPreviewConfirmationDocPayload } from './async-actions';
import { deriveConfirmationPayload, formatConfirmationDocs } from './helper';
import { deriveTokensObj } from '../confirmation-template-editor/helper';
import { SharedService } from 'app/services/SharedServices/shared-service';
import { Concatenate } from 'utils/string/string';
import i18next from 'i18next';
import { convertToFormat } from 'app/helpers/dateHelper';
import {
  closeConfirmationEditor,
  downloadSelectedConfirmationDoc,
  onConfirmationDocClick,
  onInit,
  resetEditorDirtyState,
  switchToDefaultTemplateDraftView,
  updateConfirmationDocStatusToSent,
} from './business.saga';
import { IConfirmationDoc } from './types';
import { DocSource } from '../placement-candidate-contract-doc-editor/types';

const TrackMergeTokensWrapper = (fn, ...args) =>
  trackPromise(fn(...args), 'placement-client-confirmation-templates-merge-tokens');
const TrackDraftSaveWrapper = (fn, ...args) =>
  trackPromise(fn(...args), 'placement-client-confirmation-templates-save-draft');
const TrackDeleteDraftWrapper = (fn, ...args) =>
  trackPromise(fn(...args), 'delete-placement-confirmation-draft-template');
const TrackPreviewWrapper = (fn, ...args) =>
  trackPromise(fn(...args), 'placement-client-confirmation-templates-preview-document');
const TrackPublishTokensWrapper = (fn, ...args) =>
  trackPromise(fn(...args), 'placement-client-confirmation-templates-publish-tokens');
const TrackCreateWrapper = (fn, ...args) =>
  trackPromise(fn(...args), 'placement-client-confirmation-templates-create-document');
const TrackFetchDocument = (fn, ...args) =>
  trackPromise(fn(...args), 'placement-client-confirmation-templates-fetch-document');
const TrackUpdateCCDocumentStatus = (fn, ...args) =>
  trackPromise(fn(...args), 'placement-client-confirmation-update-document-status');

export function* requestTemplates() {
  try {
    manuallyIncrementPromiseCounter('placement-client-confirmation-templates');
    const templateService: TemplateService = yield getContext('templateService');

    const response = yield call(templateService.getTemplates, true, 'Confirmation');

    if (httpSuccess(response.status)) {
      const templates = response.data.reduce((results, item) => {
        if (!item.isDraft)
          results.push({ ...item, updatedOn: convertToFormat(item.updatedOn, 'MM/DD/YYYY h:mm A', true) });
        return results;
      }, []);

      yield put(pConfirmationDocEditorActions.setTemplates(templates));

      return templates;
    }
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'requestTemplates',
        area: 'src/store/redux-store/placement-client-confirmation/saga',
      },
    });
  } finally {
    manuallyDecrementPromiseCounter('placement-client-confirmation-templates');
  }
}

export function* requestConfirmationDocuments(action: PayloadAction<{ placementId: number }>) {
  try {
    manuallyIncrementPromiseCounter('placement-client-confirmation-documents');
    const placementService: PlacementService = yield getContext('placementService');
    const [docResponse, { draftDoc }] = yield all([
      call(placementService.getClientConfirmationDocs, action.payload.placementId),
      call(requestDraftAndContacts, action),
    ]);
    let formattedDocs = [];
    if (httpSuccess(docResponse.status)) {
      const documents = docResponse.data ? docResponse.data.filter(x => x.status !== 'Draft') : [];
      if (draftDoc) documents.push(draftDoc);
      formattedDocs = formatConfirmationDocs(documents);
      yield put(pConfirmationDocEditorActions.setConfirmationDocuments(formattedDocs));
    }
    return { draftDoc, documents: formattedDocs };
  } catch (exception) {
    trackException({
      exception,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: '',
        area: 'src/store/reducx-store/placement-client-confirmation/saga',
      },
    });
    return { draftDoc: undefined, documents: [] };
  } finally {
    manuallyDecrementPromiseCounter('placement-client-confirmation-documents');
  }
}

export function* requestDraftAndContacts(action: PayloadAction<{ placementId: number }>) {
  try {
    const placementService: PlacementService = yield getContext('placementService');
    const [draftResponse, contactsResponse] = yield all([
      call(placementService.getClientConfirmationTemplateDraft, action.payload.placementId),
      call(placementService.getContacts, action.payload.placementId),
    ]);
    let draftDoc;
    if (httpSuccess(contactsResponse?.status)) {
      const contacts = contactsResponse.data.map(contact => ({
        ...contact,
        isSelected: draftResponse?.data?.placementClientConfirmationContacts?.some?.(
          x => x.contactId === contact.contactId,
        ),
      }));
      yield put(pConfirmationDocEditorActions.setContactsFeature(contacts));
    }
    if (httpSuccess(draftResponse?.status) && draftResponse.data) {
      draftDoc = { ...draftResponse.data, id: `${draftResponse.data.templateId}-draft` };
    }

    return { draftDoc };
  } catch (exception) {
    trackException({
      exception,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'requestDraftAndContacts',
        area: 'src/store/reducx-store/placement-client-confirmation/saga',
      },
    });
    return { draftDoc: undefined };
  }
}

export function* requestFacilityDefaultTemplate(action: PayloadAction<{ placementId: number }>) {
  try {
    const placementService: PlacementService = yield getContext('placementService');
    const response = yield call(placementService.getFacilityDefaultTemplate, action.payload.placementId);
    if (httpSuccess(response.status) && response?.data?.confirmationTemplateId > 0) {
      yield put(
        pConfirmationDocEditorActions.setFacilityDefaultTemplate({ templateId: response.data.confirmationTemplateId }),
      );
      return response.data.confirmationTemplateId;
    }
  } catch (exception) {
    trackException({
      exception,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'requestFacilityDefaultTemplate',
        area: 'src/store/redux-store/placement-client-confirmation/saga',
      },
    });
  }
}

export function* requestMergeTokenList() {
  try {
    const templateService: TemplateService = yield getContext('templateService');
    const response = yield call(TrackMergeTokensWrapper, templateService.getMergeTokensList, 'Confirmation');
    if (httpSuccess(response.status)) {
      const tokenObj = deriveTokensObj(response.data);
      yield put(pConfirmationDocEditorActions.setMergeTokenList(tokenObj));
    }
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'requestMergeTokenList',
        area: 'src/store/reducx-store/placement-client-confirmation/saga',
      },
    });
  }
}

export function* saveDraftTemplate(action: PayloadAction<IConfirmationDocPayload>) {
  try {
    manuallyIncrementPromiseCounter('placement-client-confirmation-templates-save-draft');
    const placementService: PlacementService = yield getContext('placementService');
    const { userInfo } = yield select(selectUser);
    const { editableContacts } = yield select(selectContacts);
    const { userHasOptedToDeleteExistingDraft } = yield select(selectPageAttributes);

    const chosenContacts = editableContacts.filter(x => x.isSelected);
    /** Draft service expects id as 0 */
    const payload = {
      ...deriveConfirmationPayload(action.payload, chosenContacts, userInfo),
      id: 0,
      markedForDelete: !!userHasOptedToDeleteExistingDraft,
    };

    const response = yield call(placementService.saveClientConfirmationTemplateDraft, payload);
    if (httpSuccess(response.status)) {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('placement.profile.clientConfirmation.setMiscellaneous.saveDraftSuccess'),
          severity: 'success',
        }),
      );

      /** Reset hard delete prop on choosing current draft */
      yield put(pConfirmationDocEditorActions.markCurrentDraftForHardDelete(false));

      let { draftDoc: newDraftDoc } = yield call(requestDraftAndContacts, {
        payload: { placementId: action.payload.placementId },
        type: 'requestDraftAndContacts',
      });
      if (newDraftDoc) {
        newDraftDoc = formatConfirmationDocs([newDraftDoc])[0];

        /** Code: Replace existing draft with new draft doc */
        const documents = _.cloneDeep<IConfirmationDoc[]>(yield select(selectDocumentList)).reduce(
          (results, doc) => {
            if (!doc.isDraft) results.push(doc);
            return results;
          },
          [newDraftDoc],
        );

        yield put(pConfirmationDocEditorActions.setConfirmationDocuments(documents));
        yield put(
          pConfirmationDocEditorActions.setSelectedTemplate({
            ...newDraftDoc,
            // @ts-ignore
            forceReset: true,
          }),
        );
        /**
         * Code: resetting dirty state since publishConfirmationDocTokens shall change the state to dirty
         * but editor state is reset within component by setSelectedTemplate
         */
        yield call(resetEditorDirtyState);
        yield put(pConfirmationDocEditorActions.setSidePanel('documents'));
      }
    } else
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('placement.profile.clientConfirmation.setMiscellaneous.saveDraftFailure'),
          severity: 'error',
        }),
      );
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'saveDraftTemplate',
        area: 'src/store/reducx-store/placement-client-confirmation/saga',
      },
    });
  } finally {
    manuallyDecrementPromiseCounter('placement-client-confirmation-templates-save-draft');
  }
}

export function* copyTemplate(action: PayloadAction<{ placementId: number, documentId: string }>) {
  try {
    manuallyIncrementPromiseCounter('placement-client-confirmation-templates-save-copy');
    const placementService: PlacementService = yield getContext('placementService');
    const { userInfo } = yield select(selectUser);

    const userId = userInfo.employeeId;
    const firstName = userInfo.firstName;
    const lastName = userInfo.lastName;

    /** Draft service expects id as 0 */
    const payload = {
      placementId: action.payload.placementId,
      documentId: action.payload.documentId,
      userId : userId,
      userName: Concatenate([firstName, lastName], ' '),
    };

    const response = yield call(placementService.copyClientConfirmationTemplateDraft, payload);
    if (httpSuccess(response.status)) {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('placement.profile.clientConfirmation.setMiscellaneous.saveCopySuccess'),
          severity: 'success',
        }),
      );

      /** Reset hard delete prop on choosing current draft */
      yield put(pConfirmationDocEditorActions.markCurrentDraftForHardDelete(false));

      let { draftDoc: newDraftDoc } = yield call(requestDraftAndContacts, {
        payload: { placementId: action.payload.placementId },
        type: 'requestDraftAndContacts',
      });
      if (newDraftDoc) {
        newDraftDoc = formatConfirmationDocs([newDraftDoc])[0];

        /** Code: Replace existing draft with new draft doc */
        const documents = _.cloneDeep<IConfirmationDoc[]>(yield select(selectDocumentList)).reduce(
          (results, doc) => {
            if (!doc.isDraft) results.push(doc);
           
            return results;
          },
          [newDraftDoc],
        );

        yield put(pConfirmationDocEditorActions.setConfirmationDocuments(documents));
        yield put(
          pConfirmationDocEditorActions.setSelectedTemplate({
            ...newDraftDoc,
            forceReset: true,
          }),
        );

        yield put(pConfirmationDocEditorActions.business_OnConfirmationDocClick({ card: newDraftDoc }));

        /**
         * Code: resetting dirty state since publishConfirmationDocTokens shall change the state to dirty
         * but editor state is reset within component by setSelectedTemplate
         */
        yield call(resetEditorDirtyState);
        yield put(pConfirmationDocEditorActions.setSidePanel('documents'));
      }
    } else
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('placement.profile.clientConfirmation.setMiscellaneous.saveCopyFailure'),
          severity: 'error',
        }),
      );
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'copyTemplate',
        area: 'src/store/reducx-store/placement-client-confirmation/saga',
      },
    });
  } finally {
    manuallyDecrementPromiseCounter('placement-client-confirmation-templates-save-copy');
  }
}

export function* deleteDraftTemplate(action: PayloadAction<{ placementId: number }>) {
  try {
    const placementService: PlacementService = yield getContext('placementService');
    const {
      userInfo: { employeeId, firstName, lastName },
    } = yield select(selectUser);

    const response = yield call(TrackDeleteDraftWrapper, placementService.deleteClientConfirmationTemplateDraft, {
      placementId: action.payload.placementId,
      userId: employeeId,
      userName: Concatenate([firstName, lastName], ' '),
    });
    if (httpSuccess(response?.status)) {
      yield put(pConfirmationDocEditorActions.setSelectedTemplate(null));
      const documents = yield select(selectDocumentList);
      const newDocs = documents.filter(doc => !doc.isDraft);
      yield put(pConfirmationDocEditorActions.setConfirmationDocuments(newDocs));

      /**
       * Code: resetting dirty state since publishConfirmationDocTokens shall change the state to dirty
       * but editor state is reset within component by setSelectedTemplate
       */
      yield call(resetEditorDirtyState);
    } else
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('placement.profile.clientConfirmation.setMiscellaneous.deleteErrorMessage'),
          severity: 'error',
        }),
      );
  } catch (error) {
    yield put(
      globalActions.setSnackBar({
        message: i18next.t('placement.profile.clientConfirmation.setMiscellaneous.deleteErrorMessage'),
        severity: 'error',
      }),
    );
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'deleteDraftTemplate',
        area: 'src/store/reducx-store/placement-client-confirmation/saga',
      },
    });
  }
}

export function* previewConfirmationDoc(action: PayloadAction<IPreviewConfirmationDocPayload>) {
  try {
    const placementService: PlacementService = yield getContext('placementService');
    const { editableContacts } = yield select(selectContacts);

    const chosenContacts = editableContacts.filter(x => x.isSelected);
    const payload = { ...action.payload, placementClientConfirmationContacts: chosenContacts ?? [] };

    const response = yield call(TrackPreviewWrapper, placementService.previewConfirmationDoc, payload);
    if (response && httpSuccess(response.status)) {
      yield put(
        pConfirmationDocEditorActions.setPreviewScreen({
          data: response.data.documentHtmlContent,
          version: response.data.version,
        }),
      );
    } else
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('placement.profile.clientConfirmation.setMiscellaneous.previewConfirmationFailure'),
          severity: 'error',
        }),
      );
  } catch (error) {
    yield put(
      globalActions.setSnackBar({
        message: i18next.t('placement.profile.clientConfirmation.setMiscellaneous.previewConfirmationFailure'),
        severity: 'error',
      }),
    );
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'previewConfirmationDoc',
        area: 'src/store/reducx-store/placement-client-confirmation/saga',
      },
    });
  }
}

export function* publishConfirmationDocTokens(action: PayloadAction<any>) {
  try {
    manuallyIncrementPromiseCounter('placement-client-confirmation-templates');
    const placementService: PlacementService = yield getContext('placementService');
    const { selectedTemplate, requestBody: payload, callback } = action.payload;

    const {
      userInfo: { employeeId, firstName, lastName },
    } = yield select(selectUser);
    const clientContacts = yield select(selectContacts);

    const response = yield call(TrackPublishTokensWrapper, placementService.publishConfirmationDocTokens, {
      ...payload,
      userId: employeeId,
      userName: Concatenate([firstName, lastName], ' '),
      placementClientConfirmationContacts: clientContacts.editableContacts?.filter(x => x.isSelected),
    });
    if (response && httpSuccess(response.status)) {
      if (callback){
        callback(response.data);
      }
    } else
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('placement.profile.clientConfirmation.setMiscellaneous.publishConfirmationTokensFailure'),
          severity: 'error',
        }),
      );
  } catch (error) {
    yield put(
      globalActions.setSnackBar({
        message: i18next.t('placement.profile.clientConfirmation.setMiscellaneous.publishConfirmationTokensFailure'),
        severity: 'error',
      }),
    );
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'publishConfirmationDocTokens',
        area: 'src/store/reducx-store/placement-client-confirmation/saga',
      },
    });
  } finally {
    manuallyDecrementPromiseCounter('placement-client-confirmation-templates');
  }
}

export function* createConfirmationDoc(action: PayloadAction<IConfirmationDocPayload>) {
  try {
    const placementService: PlacementService = yield getContext('placementService');
    const { userInfo } = yield select(selectUser);
    const { editableContacts } = yield select(selectContacts);

    const chosenContacts = editableContacts.filter(x => x.isSelected);
    const payload = deriveConfirmationPayload(action.payload, chosenContacts, userInfo);

    const response = yield call(TrackCreateWrapper, placementService.createConfirmationDoc, payload);

    if (httpSuccess(response.status)) {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('placement.profile.clientConfirmation.setMiscellaneous.creatConfirmationSuccess'),
          severity: 'success',
        }),
      );

      yield put(pConfirmationDocEditorActions.setSelectedTemplate(null));
      const [{ documents }, documentFetched] = yield all([
        call(requestConfirmationDocuments, action),
        call(requestConfirmationDocument, {
          payload: { documentId: response.data.documentId, source: DocSource.web },
          type: pConfirmationDocEditorActions.getConfirmationDoc.type,
        }),
      ]);

      const document = documents.find(x => x.documentId === response.data.documentId);

      if (documentFetched && document) {
        yield put(pConfirmationDocEditorActions.setSelectedTemplate(document));
        yield put(pConfirmationDocEditorActions.setSidePanel('documents'));

        /**
         * Code: resetting dirty state since publishConfirmationDocTokens shall change the state to dirty
         * but editor state is reset within component by setSelectedTemplate
         */
        yield call(resetEditorDirtyState);

        const urlParams = new URLSearchParams(window.location.search);

        urlParams.set('documentId', response.data.documentId);
        urlParams.delete('default_selection');

        window.history.pushState(null, null, `${window.location.pathname}?${urlParams.toString()}`);
      }
    } else
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('placement.profile.clientConfirmation.setMiscellaneous.createConfirmationFailure'),
          severity: 'error',
        }),
      );
  } catch (error) {
    yield put(
      globalActions.setSnackBar({
        message: i18next.t('placement.profile.clientConfirmation.setMiscellaneous.createConfirmationFailure'),
        severity: 'error',
      }),
    );
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'createConfirmationDoc',
        area: 'src/store/reducx-store/placement-client-confirmation/saga',
      },
    });
  }
}

export function* updateClientConfirmationDocStatus(
  action: PayloadAction<{
    placementId: number;
    documentId: string;
  }>,
) {
  try {
    const placementService: PlacementService = yield getContext('placementService');
    const {
      userInfo: { employeeId: userId, firstName, lastName },
    } = yield select(selectUser);

    const payload = {
      ...action.payload,
      userId,
      userName: Concatenate([firstName, lastName], ' '),
    };

    const response = yield call(TrackUpdateCCDocumentStatus, placementService.updateClientConfirmationDocStatus, payload);

    if (httpSuccess(response.status)) {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('placement.profile.clientConfirmation.setMiscellaneous.docStatusUpdateMessage'),
          severity: 'success',
        }),
      );
      return true;
    } else
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('placement.profile.clientConfirmation.setMiscellaneous.docStatusUpdateErrorMessage'),
          severity: 'error',
        }),
      );
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'updateClientConfirmationDocStatus',
        area: 'src/store/reducx-store/placement-client-confirmation/saga',
      },
    });
  }
}

export function* requestConfirmationDocument(
  action: PayloadAction<{ documentId: string; source: DocSource; avoidErrorMessage?: boolean }>,
) {
  try {
    const { documentId, source = DocSource.web, avoidErrorMessage } = action.payload;
    const sharedService: SharedService = yield getContext('sharedService');
    if (!!documentId) {
      const response =
        source === DocSource.classic
          ? yield call(TrackFetchDocument, sharedService.getPlacementClassicDocument, 'Confirmation', documentId)
          : yield call(TrackFetchDocument, sharedService.getPlacementDocument, documentId);
      if (httpSuccess(response.status)) {
        const document = {
          ...response.data,
          data: source === DocSource.classic ? response.data.data : response.data.filebytes,
          documentId: source === DocSource.classic ? response.data.id : response.data.documentId,
        };
        /** TODO: Move the code to where the function gets called. */
        yield put(pConfirmationDocEditorActions.setPdfView(document));
        return true;
      } else {
        if (!avoidErrorMessage)
          yield put(
            globalActions.setSnackBar({
              message:
                httpClientError(response.status) && !!response.data
                  ? response.data
                  : i18next.t('templateEditor.placement.snackbar.contractEditor.errorInFetchingDocument'),
              severity: 'error',
            }),
          );
        return null;
      }
    }
  } catch (exception) {
    if (!action.payload.avoidErrorMessage)
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('templateEditor.placement.snackbar.contractEditor.errorInFetchingDocument'),
          severity: 'error',
        }),
      );
    trackException({
      exception,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'requestConfirmationDocument',
        area: 'src/store/reducx-store/placement-client-confirmation/saga',
      },
    });
  }
}

export function* placementClientConfirmationSaga() {
  /** Business Actions - Start */
  yield takeLatest(pConfirmationDocEditorActions.onInitAction.type, onInit);
  yield takeLatest(pConfirmationDocEditorActions.business_OnConfirmationDocClick.type, onConfirmationDocClick);
  yield takeLatest(
    pConfirmationDocEditorActions.business_switchToDefaultTemplateDraftView.type,
    switchToDefaultTemplateDraftView,
  );
  yield takeLatest(pConfirmationDocEditorActions.business_closeConfirmationEditor.type, closeConfirmationEditor);
  yield takeLatest(
    pConfirmationDocEditorActions.business_downloadSelectedConfirmationtDoc.type,
    downloadSelectedConfirmationDoc,
  );
  yield takeLatest(
    pConfirmationDocEditorActions.business_updateConfirmationtDocStatusToSent.type,
    updateConfirmationDocStatusToSent,
  );
  /** Business Actions - End */
  yield takeLatest(pConfirmationDocEditorActions.getTemplates.type, requestTemplates);
  yield takeLatest(pConfirmationDocEditorActions.getMergeTokensList.type, requestMergeTokenList);
  yield takeLatest(pConfirmationDocEditorActions.saveDraftTemplate.type, saveDraftTemplate);
  yield takeLatest(pConfirmationDocEditorActions.copyTemplate.type, copyTemplate);
  yield takeLatest(pConfirmationDocEditorActions.deleteDraftTemplate.type, deleteDraftTemplate);
  yield takeLatest(pConfirmationDocEditorActions.getConfirmationDoc.type, requestConfirmationDocument);
  yield takeLatest(pConfirmationDocEditorActions.previewConfirmationDoc.type, previewConfirmationDoc);
  yield takeLatest(pConfirmationDocEditorActions.publishConfirmationDocTokens.type, publishConfirmationDocTokens);
  yield takeLatest(pConfirmationDocEditorActions.createConfirmationDoc.type, createConfirmationDoc);
  yield takeLatest(
    pConfirmationDocEditorActions.updateClientConfirmationDocStatus.type,
    updateClientConfirmationDocStatus,
  );
}
